import { type FC, type ReactNode } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'

type ConfirmVariant = 'danger' | 'warning' | 'default'

interface ConfirmDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onConfirm: () => void
    title: string
    description: ReactNode
    confirmLabel?: string
    cancelLabel?: string
    variant?: ConfirmVariant
    isLoading?: boolean
}

const iconStyles: Record<ConfirmVariant, string> = {
    danger: 'bg-red-100 text-red-600',
    warning: 'bg-orange-100 text-orange-600',
    default: 'bg-[#E5F5F9] text-[#0F172A]',
}

const confirmStyles: Record<ConfirmVariant, string> = {
    danger: 'bg-red-600 text-white hover:bg-red-700',
    warning: 'bg-orange-600 text-white hover:bg-orange-700',
    default: 'bg-[#17A2B8] text-white hover:bg-[#148c9e]',
}

/**
 * Confirmation prompt for destructive or otherwise irreversible actions.
 * Replaces `window.confirm`, which can't be styled, blocks the main thread,
 * and is suppressible by the browser.
 */
export const ConfirmDialog: FC<ConfirmDialogProps> = ({
    open,
    onOpenChange,
    onConfirm,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'danger',
    isLoading = false,
}) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[460px]">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div
                            className={cn(
                                'rounded-full p-2',
                                iconStyles[variant]
                            )}
                        >
                            <AlertTriangle className="h-4 w-4" />
                        </div>
                        <DialogTitle>{title}</DialogTitle>
                    </div>
                    <DialogDescription asChild>
                        <div className="pt-1 text-sm text-muted-foreground">
                            {description}
                        </div>
                    </DialogDescription>
                </DialogHeader>

                <DialogFooter className="gap-2 sm:gap-2">
                    <Button
                        variant="outline"
                        disabled={isLoading}
                        onClick={() => onOpenChange(false)}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        className={confirmStyles[variant]}
                        disabled={isLoading}
                        onClick={onConfirm}
                    >
                        {isLoading ? 'Working...' : confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
